/*
 * File: AdvertisingFormField.tsx
 * Project: a-a
 * File Created: Sunday, 22nd June 2025 9:50:28 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Friday, 17th October 2025 8:47:04 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2025 Mutual Mobile, Mutual Mobile
 */
import React, { useEffect, useState } from 'react';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import CustomTextField from '../../../CustomTextField/CustomTextField';
import { useInput } from '../../../../../customHook/useInput';
import {
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  Select,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CustomAutocomplete from '../../../CustomAutocomplete/CustomAutocomplete';
import zeplinColor from '../../../../../theme/Color';
import dayjs, { Dayjs } from 'dayjs';
import CustomDatePicker from '../../../CustomDatePicker/CustomDatePicker';
import CustomCheckbox from '../../../CustomCheckbox/CustomCheckbox';
import { ClientService } from '../../../../../service/client.service';
const MenuProps = {
  PaperProps: {
    sx: {
      borderRadius: '14px',
      // padding: '16px',
    },
  },
};
const AdvertisingFormField = ({
  handleInputChange,
  value,
  dealType,
  setIsValid,
}: {
  handleInputChange: (field: string, value: any) => void;
  value: Record<string, any>;
  dealType: string;
  setIsValid: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xl'));
  const {
    handleChange: handleOpportunityChange,
    handleBlur: handleOpportunityBlur,
    clearInput: clearOpportunity,
    value: Opportunity,
    invalid: invalidOpportunity,
    didChange: didOpportunityChange,
  } = useInput(value?.name ? value?.name : '', () => true);
  const {
    handleChange: handleAmountChange,
    handleBlur: handleAmountBlur,
    clearInput: clearAmount,
    value: amount,
    invalid: invalidAmount,
    didChange: didAmountChange,
  } = useInput(value?.amount ? value?.amount : '', () => true);
  const {
    handleChange: handleProbabilityChange,
    handleBlur: handleProbabilityBlur,
    clearInput: clearProbability,
    value: probability,
    invalid: invalidProbability,
    didChange: didProbabilityChange,
  } = useInput(value?.probability ? value?.probability : '', () => true);

  const {
    handleChange: handleCompensationChange,
    handleBlur: handleCompensationBlur,
    clearInput: clearCompensation,
    value: compensation,
    invalid: invalidCompensation,
    didChange: didCompensationChange,
  } = useInput(value?.compensation ? value?.compensation : '', () => true);
  const {
    handleChange: handleExclusivityChange,
    handleBlur: handleExclusivityBlur,
    clearInput: clearExclusivity,
    value: exclusivity,
    invalid: invalidExclusivity,
    didChange: didExclusivityChange,
  } = useInput(value?.exclusivityLanguage ? value?.exclusivityLanguage : '', () => true);
  const {
    handleChange: handleEstimatedChange,
    handleBlur: handleEstimatedBlur,
    clearInput: clearEstimated,
    value: estimated,
    invalid: invalidEstimated,
    didChange: didEstimatedChange,
  } = useInput(
    value?.gdEstimatedValuation
      ? value?.gdEstimatedValuation
      : value?.estimatedValuation
      ? value?.estimatedValuation
      : '',
    () => true,
  );
  const {
    handleChange: handlePartnerPercentChange,
    handleBlur: handlePartnerPercentBlur,
    clearInput: clearPartnerPercent,
    value: partnerPercent,
    invalid: invalidPartnerPercent,
    didChange: didPartnerPercentChange,
  } = useInput(value?.partnerPercent ? value?.partnerPercent : '', () => true);
  const [closeDate, setCloseDate] = useState<Dayjs | null>(
    value?.closeDate ? dayjs(value?.closeDate) : null,
  );
  const [flightStart, setFlightStart] = useState<Dayjs | null>(
    value?.flighStartDate ? dayjs(value?.flighStartDate) : null,
  );
  const [equity, setEquity] = React.useState(value?.gdEquity ? value?.gdEquity : false); // Local state for equity checkbox

  const [flightEnd, setFlightEnd] = useState<Dayjs | null>(
    value?.flightEndDate ? dayjs(value?.flightEndDate) : null,
  );
  const [drowdownList, setDropdownList] = useState<any>({});
  const [selectedItems, setSelectedItems] = useState<{
    account: any[];
    stageName: string[];
    exclusivity: string[];
    type: string[];
    category: string[];
    commission: string[];
    lostReason: string[];
    leadSource: string[];
    dealSource: string[];
  }>({
    account: value?.partnerPercent ? [value?.partnerPercent] : [],
    stageName: value?.stageName ? [value?.stageName] : [],
    exclusivity: value?.exclusivity ? [value?.exclusivity] : [],
    type: value?.type ? [value?.type] : [],
    category:
      typeof value?.category === 'string'
        ? [...value?.category?.split(';')]
        : value?.category
        ? [...value?.category]
        : [],
    commission: value?.gdCommission
      ? [value?.gdCommission]
      : value?.commission
      ? [value?.commission]
      : [],
    lostReason: value?.lostReason ? [value?.lostReason] : [],
    leadSource: value?.leadSource ? [value?.leadSource] : [],
    dealSource: value?.dealSource ? [value?.dealSource] : [],
  });
  const getDropdown = async () => {
    try {
      const res = await ClientService.getDealDropdown(dealType);
      console.log(res);
      setDropdownList(res);
    } catch (err) {
      console.log(err);
    }
  };
  useEffect(() => {
    getDropdown();
  }, []);

  useEffect(() => {
    const valid =
      !Opportunity ||
      !selectedItems?.stageName?.length ||
      !closeDate ||
      (selectedItems?.stageName[0] === 'Closed Lost' && !selectedItems?.lostReason);
    setIsValid(valid);
  }, [Opportunity, selectedItems, closeDate]);

  const handleSelection = (item: any, type: string) => {
    const key = type as keyof typeof selectedItems;
    if (Array.isArray(item)) {
      setSelectedItems((prev) => ({ ...prev, [key]: [...item] }));
    } else if (item?.target?.value) {
      const {
        target: { value },
      } = item;

      setSelectedItems((prev) => ({
        ...prev,
        [key]: typeof value === 'string' ? value.split(',') : value,
      }));
    } else {
      setSelectedItems((prev) => ({
        ...prev,
        [key]: [item],
      }));
    }
  };
  return (
    <>
      <Typography variant='heading4'>Opportunity Information</Typography>
      <CustomTextField
        form={true}
        style={{ width: '100%' }}
        onBlur={handleOpportunityBlur}
        onChange={(e) => {
          handleOpportunityChange(e);
          handleInputChange('name', e.target.value.trim());
        }}
        name='Opportunity name'
        text={Opportunity}
        error={invalidOpportunity && didOpportunityChange ? 'Please enter valid Opportunity' : ''}
        label={'Opportunity name'}
        type='text'
        tabindex={1}
        required
      />
      {/* <FormControl sx={{ width: '100%' }}>
        <CustomAutocomplete
          required={true}
          options={[{ Name: 'Marketing', Id: '1' }]}
          getOptionLabel={(option: any) => (option?.Name ? option?.Name : '')}
          label={'Account name'}
          handleDeleteItems={() => {}}
          selectedItems={selectedItems}
          handleSelection={(value) => {
            handleSelection(value, 'account');
            handleInputChange('accountId', value);
          }}
          value={selectedItems?.account[0]}
          handleFilter={() => {}}
          placeholder='Search people'
          currentLength={[{ Name: 'Marketing', Id: '1' }]?.length}
          totalLength={[{ Name: 'Marketing', Id: '1' }]?.length}
          next={() => {}}
          multiple={false}
        />
      </FormControl> */}
      <Box sx={{ display: 'flex', gap: '24px', width: '100%' }}>
        <FormControl sx={{ width: '100%' }}>
          <InputLabel
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name-label'
            sx={{
              '&.Mui-focused': { color: zeplinColor.Neutral9 },
              fontSize: isXs ? '14px' : '16px',
            }}
          >
            Stage<span style={{ color: zeplinColor.Error }}>*</span>
          </InputLabel>
          <Select
            labelId='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name'
            // multiple
            required={true}
            value={selectedItems.stageName}
            onChange={(e) => {
              handleSelection(e, 'stageName');
              handleInputChange('stageName', e.target.value);
            }}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    color: zeplinColor.Neutral9,
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='Stage'
              />
            }
            renderValue={
              (selected) =>
                // (selected as string[]).map((val) => (
                {
                  return selected?.length ? (
                    drowdownList?.StageName?.length &&
                    drowdownList?.StageName.filter((val: any) => val === selected[0])[0] ? (
                      drowdownList?.StageName.filter((val: any) => val === selected[0])[0]
                    ) : (
                      <Skeleton
                        variant='text'
                        sx={{
                          fontSize: theme.typography.body2,
                          width: '100%',
                          // Optionally adjust other styles here
                        }}
                      />
                    )
                  ) : (
                    ''
                  );
                }
              // ))
            }
            MenuProps={MenuProps}
            IconComponent={KeyboardArrowDown}
          >
            {drowdownList?.StageName?.length &&
              drowdownList?.StageName.map((name: any) => (
                <MenuItem key={name} value={name}>
                  <Radio
                    checked={
                      selectedItems?.stageName?.length
                        ? selectedItems?.stageName?.indexOf(name) > -1
                        : false
                    } // Check if this radio should be selected
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': {
                        color: zeplinColor.BasicBlack,
                      },
                    }}
                  />
                  {name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <CustomDatePicker
          value={closeDate}
          size={isXs ? 'small' : 'medium'}
          setValue={(date) => {
            setCloseDate(date); // Update local state
            handleInputChange('closeDate', dayjs(date as Dayjs).format('YYYY-MM-DD')); // Pass the selected date to the parent component
          }}
          label={'Close date'}
          required={true}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: '24px', width: '100%' }}>
        <CustomTextField
          disable={true}
          form={true}
          style={{ width: '100%' }}
          onBlur={() => {
            handleAmountBlur && handleAmountBlur();
          }}
          onChange={(e) => {
            handleAmountChange && handleAmountChange(e);
            handleInputChange('amount', +e.target.value);
          }}
          name={'amount'}
          text={amount ? amount : ''}
          error={invalidAmount && didAmountChange ? 'Please enter valid Amount' : ''}
          label={'Amount'}
          type='number'
          tabindex={1}
          // required
        />

        <CustomTextField
          form={true}
          style={{ width: '100%' }}
          onBlur={() => {
            handleProbabilityBlur && handleProbabilityBlur();
          }}
          onChange={(e) => {
            handleProbabilityChange && handleProbabilityChange(e);
            handleInputChange('probability', +e.target.value);
          }}
          name={'Probability %'}
          text={probability ? probability : ''}
          error={invalidProbability && didProbabilityChange ? 'Please enter valid Probability' : ''}
          label={'Probability %'}
          type='number'
          tabindex={2}
          max={100}
          // required
        />
      </Box>
      {/* <CustomCheckbox
        label='Equity'
        checked={equity} // Controlled state
        onChange={(checked) => {
          setEquity(checked); // Update local state
          handleInputChange('gdEquity', checked); // Pass the updated state to the parent component
        }}
      /> */}
      <Typography variant='heading4'>Deal Points</Typography>
      {/* <Box sx={{ display: 'flex', gap: '24px', width: '100%' }}>
        <CustomDatePicker
          value={flightStart}
          setValue={(date) => {  
            setFlightStart(date);
            handleInputChange('flightStartDate', dayjs(date as Dayjs).format('YYYY-MM-DD'));
          }}
          label={'Flight date start'}
        />

        <CustomDatePicker
          value={flightEnd}
          setValue={(date) => {
            setFlightEnd(date);
            handleInputChange('flightEndDate', dayjs(date as Dayjs).format('YYYY-MM-DD'));
          }}
          label={'Flight date end'}
        />
      </Box> */}
      <Box sx={{ display: 'flex', gap: '24px', width: '100%' }}>
        <FormControl sx={{ width: '100%' }}>
          <InputLabel
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name-label'
            sx={{
              '&.Mui-focused': { color: zeplinColor.Neutral9 },
              fontSize: isXs ? '14px' : '16px',
            }}
          >
            Deal source<span style={{ color: zeplinColor.Error }}></span>
          </InputLabel>
          <Select
            labelId='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name'
            // multiple
            required={true}
            value={selectedItems.dealSource}
            onChange={(e) => {
              handleSelection(e, 'dealSource');
              handleInputChange('dealSource', e.target.value);
            }}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    color: zeplinColor.Neutral9,
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='Deal source'
              />
            }
            renderValue={
              (selected) =>
                // (selected as string[]).map((val) => (
                {
                  return selected?.length ? (
                    drowdownList?.GD_DealSource__c?.length &&
                    drowdownList?.GD_DealSource__c.filter((val: any) => val === selected[0])[0] ? (
                      drowdownList?.GD_DealSource__c.filter((val: any) => val === selected[0])[0]
                    ) : (
                      <Skeleton
                        variant='text'
                        sx={{
                          fontSize: theme.typography.body2,
                          width: '100%',
                          // Optionally adjust other styles here
                        }}
                      />
                    )
                  ) : (
                    ''
                  );
                }
              // ))
            }
            MenuProps={MenuProps}
            IconComponent={KeyboardArrowDown}
          >
            {drowdownList?.GD_DealSource__c?.length &&
              drowdownList?.GD_DealSource__c.map((name: any) => (
                <MenuItem key={name} value={name}>
                  <Radio
                    checked={
                      selectedItems?.dealSource?.length
                        ? selectedItems?.dealSource?.indexOf(name) > -1
                        : false
                    } // Check if this radio should be selected
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': {
                        color: zeplinColor.BasicBlack,
                      },
                    }}
                  />
                  {name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <FormControl sx={{ width: '100%' }}>
          <InputLabel
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name-label'
            sx={{
              '&.Mui-focused': { color: zeplinColor.Neutral9 },
              fontSize: isXs ? '14px' : '16px',
            }}
          >
            Exclusivity<span style={{ color: zeplinColor.Error }}></span>
          </InputLabel>
          <Select
            labelId='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name'
            // multiple
            required={true}
            value={selectedItems.exclusivity}
            onChange={(e) => {
              handleSelection(e, 'exclusivity');
              handleInputChange('exclusivity', e.target.value);
            }}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    color: zeplinColor.Neutral9,
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='Exclusivity'
              />
            }
            renderValue={
              (selected) =>
                // (selected as string[]).map((val) => (
                {
                  return selected?.length ? (
                    drowdownList?.Exclusivity__c?.length &&
                    drowdownList?.Exclusivity__c?.filter((val: any) => val === selected[0])[0] ? (
                      drowdownList?.Exclusivity__c?.filter((val: any) => val === selected[0])[0]
                    ) : (
                      <Skeleton
                        variant='text'
                        sx={{
                          fontSize: theme.typography.body2,
                          width: '100%',
                          // Optionally adjust other styles here
                        }}
                      />
                    )
                  ) : (
                    ''
                  );
                }
              // ))
            }
            MenuProps={MenuProps}
            IconComponent={KeyboardArrowDown}
          >
            {drowdownList?.Exclusivity__c?.length &&
              drowdownList?.Exclusivity__c?.map((name: any) => (
                <MenuItem key={name} value={name}>
                  <Radio
                    checked={
                      selectedItems?.exclusivity?.length
                        ? selectedItems?.exclusivity?.indexOf(name) > -1
                        : false
                    } // Check if this radio should be selected
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': {
                        color: zeplinColor.BasicBlack,
                      },
                    }}
                  />
                  {name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        {/* <FormControl sx={{ width: '100%' }}>
          <InputLabel
            id='demo-multiple-name-label'
            sx={{
              '&.Mui-focused': { color: zeplinColor.Neutral9 },
            }}
          >
            Type<span style={{ color: zeplinColor.Error }}></span>
          </InputLabel>
          <Select
            labelId='demo-multiple-name-label'
            id='demo-multiple-name'
            // multiple
            required={true}
            value={selectedItems.type}
            onChange={(e) => handleSelection(e, 'type')}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    color: zeplinColor.Neutral9,
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='Type'
              />
            }
            renderValue={
              (selected) =>
                // (selected as string[]).map((val) => (
                {
                  return selected?.length
                    ? typeList?.length && typeList.filter((val) => val.id === selected[0])[0]?.name
                    : '';
                }
              // ))
            }
            MenuProps={MenuProps}
            IconComponent={KeyboardArrowDown}
          >
            {typeList?.length &&
              typeList.map((name) => (
                <MenuItem key={name.id} value={name.id}>
                  <Radio
                    checked={
                      selectedItems?.stage?.length
                        ? selectedItems?.stage?.indexOf(name.id) > -1
                        : false
                    } // Check if this radio should be selected
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': {
                        color: zeplinColor.BasicBlack,
                      },
                    }}
                  />
                  {name.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl> */}
      </Box>
      <Box sx={{ display: 'flex', gap: '24px', width: '100%' }}>
        <FormControl sx={{ width: '100%' }}>
          <InputLabel
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name-label'
            sx={{
              '&.Mui-focused': { color: zeplinColor.Neutral9 },
              fontSize: isXs ? '14px' : '16px',
            }}
          >
            Type<span style={{ color: zeplinColor.Error }}></span>
          </InputLabel>
          <Select
            labelId='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name'
            // multiple
            required={true}
            value={selectedItems.type}
            onChange={(e) => {
              handleSelection(e, 'type');
              handleInputChange('type', e.target.value);
            }}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    color: zeplinColor.Neutral9,
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='Type'
              />
            }
            renderValue={
              (selected) =>
                // (selected as string[]).map((val) => (
                {
                  return selected?.length ? (
                    drowdownList?.Type?.length &&
                    drowdownList?.Type?.filter((val: any) => val === selected[0])[0] ? (
                      drowdownList?.Type?.filter((val: any) => val === selected[0])[0]
                    ) : (
                      <Skeleton
                        variant='text'
                        sx={{
                          fontSize: theme.typography.body2,
                          width: '100%',
                          // Optionally adjust other styles here
                        }}
                      />
                    )
                  ) : (
                    ''
                  );
                }
              // ))
            }
            MenuProps={MenuProps}
            IconComponent={KeyboardArrowDown}
          >
            {drowdownList?.Type?.length &&
              drowdownList?.Type?.map((name: any) => (
                <MenuItem key={name} value={name}>
                  <Radio
                    checked={
                      selectedItems?.type?.length ? selectedItems?.type?.indexOf(name) > -1 : false
                    } // Check if this radio should be selected
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': {
                        color: zeplinColor.BasicBlack,
                      },
                    }}
                  />
                  {name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <FormControl sx={{ width: '100%' }}>
          <InputLabel
            id='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            sx={{
              '&.Mui-focused': { color: zeplinColor.Neutral9 },
              fontSize: isXs ? '14px' : '16px',
            }}
          >
            Category<span style={{ color: zeplinColor.Error }}></span>
          </InputLabel>
          <Select
            labelId='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name'
            multiple
            required={true}
            value={selectedItems.category}
            onChange={(e) => {
              handleSelection(e, 'category');
              handleInputChange('category', e.target.value);
            }}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    color: zeplinColor.Neutral9,
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='Category'
              />
            }
            renderValue={
              (selected) =>
                // (selected as string[]).map((val) => (
                {
                  return selected?.length ? (
                    drowdownList?.Category__c?.length &&
                    drowdownList?.Category__c?.filter((val: any) => selected?.includes(val))
                      ?.length ? (
                      drowdownList?.Category__c?.filter((val: any) =>
                        selected?.includes(val),
                      )?.join(', ')
                    ) : (
                      <Skeleton
                        variant='text'
                        sx={{
                          fontSize: theme.typography.body2,
                          width: '100%',
                          // Optionally adjust other styles here
                        }}
                      />
                    )
                  ) : (
                    ''
                  );
                }
              // ))
            }
            MenuProps={MenuProps}
            IconComponent={KeyboardArrowDown}
          >
            {drowdownList?.Category__c?.length &&
              drowdownList?.Category__c?.map((name: any) => (
                <MenuItem key={name} value={name}>
                  <Checkbox
                    checked={
                      selectedItems?.category?.length
                        ? selectedItems?.category?.indexOf(name) > -1
                        : false
                    }
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': {
                        color: zeplinColor.BasicBlack,
                      },
                    }}
                  />
                  {name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        {/* <FormControl sx={{ width: '100%' }}>
          <InputLabel
            id='demo-multiple-name-label'
            sx={{
              '&.Mui-focused': { color: zeplinColor.Neutral9 },
            }}
          >
            Commission<span style={{ color: zeplinColor.Error }}></span>
          </InputLabel>
          <Select
            labelId='demo-multiple-name-label'
            id='demo-multiple-name'
            // multiple
            required={true}
            value={selectedItems.commission}
            onChange={(e) => handleSelection(e, 'commission')}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    color: zeplinColor.Neutral9,
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='Commision'
              />
            }
            renderValue={
              (selected) =>
                // (selected as string[]).map((val) => (
                {
                  return selected?.length
                    ? commisionList?.length &&
                        commisionList.filter((val) => val.id === selected[0])[0]?.name
                    : '';
                }
              // ))
            }
            MenuProps={MenuProps}
            IconComponent={KeyboardArrowDown}
          >
            {commisionList?.length &&
              commisionList.map((name) => (
                <MenuItem key={name.id} value={name.id}>
                  <Radio
                    checked={
                      selectedItems?.stage?.length
                        ? selectedItems?.stage?.indexOf(name.id) > -1
                        : false
                    } // Check if this radio should be selected
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': {
                        color: zeplinColor.BasicBlack,
                      },
                    }}
                  />
                  {name.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl> */}
      </Box>
      {/* <Typography variant='heading4'>Services</Typography>
      <CustomTextField
        style={{ width: '100%' }}
        onBlur={handleServicesBlur}
        onChange={(e) => {
          handleServicesChange(e);
          handleInputChange('services', e.target.value);
        }}
        name='Services name'
        text={services}
        error={invalidServices && didServicesChange ? 'Please enter valid Services' : ''}
        label={'Services name'}
        type='text'
        tabindex={1}
        // required
        multiline
      /> */}
      <Typography variant='heading4'>Terms</Typography>
      <CustomTextField
        form={true}
        style={{ width: '100%' }}
        onBlur={handleCompensationBlur}
        onChange={(e) => {
          handleCompensationChange(e);
          handleInputChange('compensation', e.target.value);
        }}
        name='Compensation'
        text={compensation}
        error={
          invalidCompensation && didCompensationChange ? 'Please enter valid Compensation' : ''
        }
        label={'Compensation'}
        type='text'
        tabindex={1}
        multiline
        // required
      />
      <CustomTextField
        form={true}
        style={{ width: '100%' }}
        onBlur={handleExclusivityBlur}
        onChange={(e) => {
          handleExclusivityChange(e);
          handleInputChange('exclusivityLanguage', e.target.value);
        }}
        name='Exclusivity language'
        text={exclusivity}
        error={invalidExclusivity && didExclusivityChange ? 'Please enter valid Exclusivity' : ''}
        label={'Exclusivity language'}
        type='text'
        tabindex={1}
        multiline
        // required
      />
      <Typography variant='heading4'>Additional Information</Typography>
      <Box sx={{ display: 'flex', gap: '24px', width: '100%' }}>
        {equity && (
          <CustomTextField
            form={true}
            style={{ width: '100%' }}
            onBlur={() => {
              handleEstimatedBlur && handleEstimatedBlur();
            }}
            onChange={(e) => {
              handleEstimatedChange && handleEstimatedChange(e);
              handleInputChange('gdEstimatedValuation', +e.target.value);
            }}
            name={'Estimated Valuation'}
            text={estimated ? estimated : ''}
            error={invalidEstimated && didEstimatedChange ? 'Please enter valid Estimated' : ''}
            label={'Estimated Valuation'}
            type='number'
            tabindex={1}
            // max={100}
            // required
          />
        )}
        <CustomTextField
          form={true}
          style={{ width: '100%' }}
          onBlur={() => {
            handlePartnerPercentBlur && handlePartnerPercentBlur();
          }}
          onChange={(e) => {
            handlePartnerPercentChange && handlePartnerPercentChange(e);
            handleInputChange('partnerPercent', +e.target.value);
          }}
          name={'Partner percent (%)'}
          text={partnerPercent ? partnerPercent : ''}
          error={
            invalidPartnerPercent && didPartnerPercentChange
              ? 'Please enter valid PartnerPercent'
              : ''
          }
          label={'Partner percent (%)'}
          type='number'
          tabindex={1}
          max={100}
          // required
        />
      </Box>
      <Box sx={{ display: 'flex', gap: '24px', width: '100%' }}>
        {selectedItems?.stageName.includes('Closed Lost') && (
          <FormControl sx={{ width: '100%' }}>
            <InputLabel
              size={isXs ? 'small' : 'medium'}
              id='demo-multiple-name-label'
              sx={{
                '&.Mui-focused': { color: zeplinColor.Neutral9 },
                fontSize: isXs ? '14px' : '16px',
              }}
            >
              Reason Lost<span style={{ color: zeplinColor.Error }}></span>
            </InputLabel>
            <Select
              labelId='demo-multiple-name-label'
              size={isXs ? 'small' : 'medium'}
              id='demo-multiple-name'
              // multiple
              required={true}
              value={selectedItems.lostReason}
              onChange={(e) => {
                handleSelection(e, 'lostReason');
                handleInputChange('reasonLost', e.target.value);
              }}
              input={
                <OutlinedInput
                  sx={{
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      color: zeplinColor.Neutral9,
                      borderColor: zeplinColor.Neutral9,
                    },
                  }}
                  label='lostReason'
                />
              }
              renderValue={
                (selected) =>
                  // (selected as string[]).map((val) => (
                  {
                    return selected?.length ? (
                      drowdownList?.Reason_Lost__c?.length &&
                      drowdownList?.Reason_Lost__c?.filter((val: any) => val === selected[0])[0] ? (
                        drowdownList?.Reason_Lost__c?.filter((val: any) => val === selected[0])[0]
                      ) : (
                        <Skeleton
                          variant='text'
                          sx={{
                            fontSize: theme.typography.body2,
                            width: '100%',
                            // Optionally adjust other styles here
                          }}
                        />
                      )
                    ) : (
                      ''
                    );
                  }
                // ))
              }
              MenuProps={MenuProps}
              IconComponent={KeyboardArrowDown}
            >
              {drowdownList?.Reason_Lost__c?.length &&
                drowdownList?.Reason_Lost__c?.map((name: any) => (
                  <MenuItem key={name} value={name}>
                    <Radio
                      checked={
                        selectedItems?.lostReason?.length
                          ? selectedItems?.lostReason?.indexOf(name) > -1
                          : false
                      } // Check if this radio should be selected
                      sx={{
                        color: zeplinColor.BasicBlack,
                        '&.Mui-checked': {
                          color: zeplinColor.BasicBlack,
                        },
                      }}
                    />
                    {name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}
        <FormControl sx={{ width: '100%' }}>
          <InputLabel
            id='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            sx={{
              '&.Mui-focused': { color: zeplinColor.Neutral9 },
              fontSize: isXs ? '14px' : '16px',
            }}
          >
            Lead source<span style={{ color: zeplinColor.Error }}></span>
          </InputLabel>
          <Select
            labelId='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name'
            // multiple
            required={true}
            value={selectedItems.leadSource}
            onChange={(e) => {
              handleSelection(e, 'leadSource');
              handleInputChange('leadSource', e.target.value);
            }}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    color: zeplinColor.Neutral9,
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='leadSource'
              />
            }
            renderValue={
              (selected) =>
                // (selected as string[]).map((val) => (
                {
                  return selected?.length ? (
                    drowdownList?.LeadSource?.length &&
                    drowdownList?.LeadSource?.filter((val: any) => val === selected[0])[0] ? (
                      drowdownList?.LeadSource?.filter((val: any) => val === selected[0])[0]
                    ) : (
                      <Skeleton
                        variant='text'
                        sx={{
                          fontSize: theme.typography.body2,
                          width: '100%',
                          // Optionally adjust other styles here
                        }}
                      />
                    )
                  ) : (
                    ''
                  );
                }
              // ))
            }
            MenuProps={MenuProps}
            IconComponent={KeyboardArrowDown}
          >
            {drowdownList?.LeadSource?.length &&
              drowdownList?.LeadSource?.map((name: any) => (
                <MenuItem key={name} value={name}>
                  <Radio
                    checked={
                      selectedItems?.leadSource?.length
                        ? selectedItems?.leadSource?.indexOf(name) > -1
                        : false
                    } // Check if this radio should be selected
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': {
                        color: zeplinColor.BasicBlack,
                      },
                    }}
                  />
                  {name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Box>
    </>
  );
};

export default AdvertisingFormField;
